import { CompetenceDownloadDto, CompetenceEntry, ControllerHelper, MailTemplateDownloadDto, MediaController, PlaylistAssignToPlaylistTrigger, PlaylistCertificateTrigger, PlaylistSendMailOnAssignmentTrigger, PlaylistTrigger, PlaylistUpdateCompetenciesTrigger, PostDownloadDto, valuesPlaylistTriggerKind } from "collaboration-service";
import ArraySelection from "components/ArraySelection";
import CancelWithConfirmation from "components/CancelWithConfirmation";
import MailTemplateSelection from "components/MailTemplates/MailTemplateSelection";
import { Button, Checkbox, getColor, getUriFromLinkByName, Image, ImgSelect, Input, LoaderInline, styled, useThemePart } from "imaginarity-react-ui";
import * as _ from "lodash";
import * as React from "react";
import { useAppSelector } from "services/ApplicationState/ApplicationState";
import { stripHtml } from "services/Helpers";
import { getTranslated } from "services/Helpers/TranslationHelpers";
import { useImgI18N } from "services/ImgI18N";

type TriggerKind = "certificates" | "playlists" | "competencies";

interface PlaylistEditFinishedTriggerProps {
    trigger: PlaylistTrigger;
    onUpdate: (trigger: PlaylistTrigger) => void;
    posts?: PostDownloadDto[];
    search: (kind: TriggerKind, callback: (post: PostDownloadDto) => void) => void;
    competences?: CompetenceDownloadDto[];
}

const C = styled.div`
    padding: 10px;
    display: grid;
    grid-template-columns: minmax(200px, max-content) 1fr;
    gap: 5px 40px;
    align-items: start;
`;
const Lh40 = styled.div`
    line-height: 40px;
`;


interface TProps<T> {
    trigger: T;
    search: (kind: TriggerKind, callback: (post: PostDownloadDto) => void) => void;
    posts?: PostDownloadDto[];

}

interface PCTProps extends TProps<PlaylistCertificateTrigger> {
    onUpdate: (ids: string[]) => void;
}

interface PPTProps extends TProps<PlaylistAssignToPlaylistTrigger> {
    onUpdate: (id: string, optional?: boolean, done?: boolean) => void;
}

interface PMTProps extends TProps<PlaylistSendMailOnAssignmentTrigger> {
    onUpdate: (id: string) => void;
}

interface PCOTProps extends TProps<PlaylistUpdateCompetenciesTrigger> {
    onUpdate: (updated: CompetenceEntry[]) => void;
    competences?: CompetenceDownloadDto[];
}


const PCTGrid = styled.div`
    display: grid;
    grid-template-columns: 40px 1fr max-content;
    align-items: center;
    column-gap: 10px;
    margin-bottom: 2px;
    transition: all 0.2s ease-in-out;
    
    line-height: 40px;
    &:hover{
        background: ${p => getColor(p, "@veryLightGrey")};
    }
`;

const PPTGrid = styled.div`
    display: grid;
    grid-template-columns: 40px 1fr;
    align-items: center;
    column-gap: 10px;
    margin-bottom: 2px;
    transition: all 0.2s ease-in-out;
    
    line-height: 40px;
    &:hover{
        background: ${p => getColor(p, "@veryLightGrey")};
    }
`;

const PCT = (p: PCTProps) => {
    const contentLanguage = useAppSelector(s => s.contentLanguage);
    const { trigger, onUpdate, search, posts } = p;
    const { t } = useImgI18N("playlist");
    const empty = useThemePart(t => t.emptyImageUrl)
    const up = React.useMemo(() => (toSet: PostDownloadDto) => {
        if (toSet) {
            const found = _.find(trigger.certificateIds, c => c === toSet.id);
            if (!found) {
                onUpdate([...(trigger.certificateIds ?? []), toSet.id]);
            }
        }
    }, [onUpdate, trigger.certificateIds]);

    const hasCertificates = React.useMemo(() => {
        return trigger.certificateIds && trigger.certificateIds.length > 0;
    }, [trigger.certificateIds]);
    return (
        <>
            <div />
            <div style={{ gridColumnStart: 2, gridColumnEnd: 3 }}>
                <Button floated="left" kind={hasCertificates ? "halfTransparentButton" : "primary"} icon="search" content={t("search certificates")} onClick={() => search("certificates", up)} />
            </div>
            <Lh40>
                {t("certificates to assign")}
            </Lh40>
            <div>
                {hasCertificates ?
                    _.map(trigger.certificateIds, (c, i) => {
                        const post = _.find(posts, p => p.id === c);
                        const imgUrl = getUriFromLinkByName(post?.media, "self") ?? empty;
                        return (
                            <PCTGrid key={i}>
                                <Image src={imgUrl} squared width="40px" />
                                <div>
                                    {stripHtml(getTranslated(post?.headlines, contentLanguage)?.text ?? "")}
                                </div>
                                <CancelWithConfirmation
                                    cancel={() => onUpdate(_.without(trigger.certificateIds, c))}
                                    floated="left"
                                    iconOnly
                                    kind="transparentButton"
                                    tooltipInitialButton="remove certificate"
                                    confirmText="yes, remove certificate"
                                    icon="delete"
                                    iconColor="@accentRed"
                                />
                            </PCTGrid>
                        )
                    })
                    :
                    <Lh40>
                        {t("no certificates assigned yet")}
                    </Lh40>
                }
            </div>

        </>

    );
}

const PPT = (p: PPTProps) => {
    const contentLanguage = useAppSelector(s => s.contentLanguage);
    const { trigger, onUpdate, search, posts } = p;
    const { t } = useImgI18N("playlist");
    const empty = useThemePart(t => t.emptyImageUrl)


    const up = React.useMemo(() => (toSet: PostDownloadDto) => {
        if (toSet) {
            if (toSet.id !== trigger.playlistId)
                onUpdate(toSet.id, trigger?.assignOptional, trigger?.assignDone);
        }
    }, [onUpdate, trigger.playlistId, trigger?.assignOptional, trigger?.assignDone]);

    const post = _.find(posts, p => p.id === trigger.playlistId);
    const imgUrl = getUriFromLinkByName(post?.media, "self") ?? empty;

    const hasPlaylist = React.useMemo(() => {
        return trigger.playlistId && trigger.playlistId.length > 0;
    }, [trigger.playlistId]);

    return (
        <>
            <div />
            <div style={{ gridColumnStart: 2, gridColumnEnd: 3 }}>
                <Button floated="left" kind={hasPlaylist ? "halfTransparentButton" : "primary"} icon="search" content={t("search playlist")} onClick={() => search("playlists", up)} />
            </div>

            <Lh40>
                {t("playlist to assign")}
            </Lh40 >
            <PPTGrid>
                <Image src={imgUrl} squared width="40px" />
                <Lh40>
                    {hasPlaylist ? stripHtml(getTranslated(post?.headlines, contentLanguage)?.text ?? "") : t("no playlist assigned yet")}
                </Lh40>
            </PPTGrid>

            {
                hasPlaylist &&
                <>

                    <Lh40>
                        {t("assign playlist")}
                    </Lh40>
                    <div style={{ transform: "translate(-2px, 7px)" }}>
                        <Checkbox label="as optional" onClick={() => onUpdate(trigger.playlistId ?? "", !(trigger?.assignOptional ?? false), trigger.assignDone)} selected={trigger?.assignOptional} />
                    </div>
                    <div style={{ lineHeight: "10px" }}>
                        {t("assign playlist")}
                    </div>
                    <div style={{ transform: "translate(-2px, -12px)" }}>
                        <Checkbox label="as done" onClick={() => onUpdate(trigger.playlistId ?? "", trigger?.assignOptional, !(trigger.assignDone ?? false))} selected={trigger?.assignDone} />
                    </div>
                </>
            }
        </>

    );
}

const PMT = (p: PMTProps) => {
    const contentLanguage = useAppSelector(s => s.contentLanguage);
    const groupId = useAppSelector(s => s.currentGroup?.id);
    const { trigger, onUpdate } = p;
    const { t } = useImgI18N("playlist");
    const [open, setOpen] = React.useState(false);
    const [mail, setMail] = React.useState<MailTemplateDownloadDto>();


    React.useEffect(() => {
        const load = async () => {
            if (trigger.mailTemplateId) {
                const res = await ControllerHelper.singleCall({ id: trigger.mailTemplateId }, MediaController.GetMailTemplate, true);
                setMail(res);
            }
        };
        load();
    }, [trigger.mailTemplateId]);

    const upm = React.useMemo(() => (template?: MailTemplateDownloadDto) => {
        onUpdate(template?.id ?? "");
        setOpen(false);
    }, [onUpdate]);
    return (
        <>
            <div />
            <div style={{ gridColumnStart: 2, gridColumnEnd: 3 }}>
                <Button floated="left" kind={"primary"} icon="search" content={t("search mail template")} onClick={() => setOpen(true)} />
            </div>


            <Lh40>
                {t("mail template to use")}
            </Lh40 >

            <PPTGrid>
                <div />
                <Lh40>
                    {mail ? stripHtml(getTranslated(mail?.names, contentLanguage)?.text ?? "") : t("no mail template assigned yet")}
                </Lh40>
            </PPTGrid>

            <div>
                <MailTemplateSelection
                    onSelect={upm}
                    active={open}
                    groupId={groupId}
                />
            </div>
        </>

    );
}

const COTGrid = styled.div`
    display: grid;
    grid-template-columns: max-content 150px 40px 1fr max-content;
    align-items: center;
    column-gap: 10px;
    margin-bottom: 2px;
    transition: all 0.2s ease-in-out;
    &:hover{
        background: ${p => getColor(p, "@veryLightGrey")};
    }
`;

const PCOT = (p: PCOTProps) => {
    const contentLanguage = useAppSelector(s => s.contentLanguage);
    const { trigger, onUpdate, competences } = p;
    const { t } = useImgI18N("playlist");
    const empty = useThemePart(t => t.emptyImageUrl)

    const compOpts = React.useMemo(() => {
        return _.map(_.filter(competences, cc => _.find(trigger.competenceUpdates, up => up.competenceId === cc.id) === undefined), c => ({ value: c, label: getTranslated(c.names, contentLanguage).text ?? "" }));
    }, [competences, trigger.competenceUpdates, contentLanguage]);

    const up = React.useMemo(() => (toSet: CompetenceEntry) => {
        const toChg = _.find(trigger.competenceUpdates, c => c.competenceId === toSet.competenceId);
        if (toChg) {
            toChg.value = Math.max(0, Math.min(10, toSet.value));
        }
        onUpdate(_.clone(trigger.competenceUpdates ?? []))
    }, [onUpdate, trigger.competenceUpdates]);

    const add = React.useMemo(() => (toAdd: { value: CompetenceDownloadDto, label: string }) => {
        onUpdate([...(trigger.competenceUpdates ?? []), {
            competenceId: toAdd.value.id,
            value: 0, // default value, can be changed later     
            timeStamp: new Date(),
        }]);
    }, [onUpdate, trigger.competenceUpdates]);

    const del = React.useMemo(() => (toDel: CompetenceEntry) => () => {
        onUpdate(_.without(trigger.competenceUpdates, toDel));
    }, [onUpdate, trigger.competenceUpdates]);

    return (
        <>
            <Lh40>
                {t("add competencies to update")}
            </Lh40>
            <ImgSelect
                options={compOpts}
                onChange={add}
                isMulti={false}
            />
            <Lh40>
                {t("competencies to update")}
            </Lh40>
            <div>
                {_.map(trigger.competenceUpdates, (c, i) => {
                    const comp = _.find(competences, cc => cc.id === c.competenceId);
                    const imgUrl = getUriFromLinkByName(comp?.media, "self") ?? empty;
                    return (
                        <COTGrid key={i}>

                            <div style={{ position: "relative", height: 40, width: 100 }}>
                                <Input
                                    type="number"
                                    min={0.0}
                                    max={10.0}
                                    step={0.1}
                                    value={c.value}
                                    debounce={500}
                                    onChange={(e) => {
                                        const newValue = parseFloat(e.target.value);
                                        if (!isNaN(newValue)) {
                                            up({ ...c, value: newValue });
                                        }
                                    }}
                                />
                            </div>
                            <div style={{ textAlign: "center" }}>
                                {t("initial value {{value}}", { value: comp?.defaultValue ?? 0 })}<br />
                            </div>
                            <Image src={imgUrl} squared width="40px" />
                            <div>
                                {stripHtml(getTranslated(comp?.names, contentLanguage)?.text ?? "")}
                            </div>
                            <div>
                                <CancelWithConfirmation cancel={del(c)} icon="delete" iconOnly tooltipInitialButton={t("remove competency")} confirmText={t("yes, remove competency")} cancelText={t("cancel")} kind='transparentButton' floated='left' iconColor="@accentRed" />
                            </div>
                        </COTGrid>
                    )
                })}
            </div >


        </>

    );
}


const PlaylistEditFinishedTrigger = (p: PlaylistEditFinishedTriggerProps) => {
    const { trigger, onUpdate, search, posts, competences } = p;
    const { t } = useImgI18N("playlist");

    return (
        <C>
            <Lh40>
                {t("playlist trigger kind")}
            </Lh40>
            <div>
                <ArraySelection
                    array={valuesPlaylistTriggerKind}
                    isMulti={false}
                    value={trigger.type as string}
                    onChange={(v) => onUpdate({ ...trigger, type: v } as PlaylistTrigger)}
                    namespace="playlist"
                />
            </div>
            {trigger.type === "PlaylistCertificateTrigger" &&
                <>
                    {!posts ? <LoaderInline active={true} /> :
                        <PCT
                            trigger={trigger}
                            onUpdate={(ids) => onUpdate({ ...trigger, type: "PlaylistCertificateTrigger", certificateIds: ids } as PlaylistCertificateTrigger)}
                            search={search}
                            posts={posts}
                        />
                    }
                </>
            }
            {trigger.type === "PlaylistAssignToPlaylistTrigger" &&
                <>
                    {!posts ? <LoaderInline active={true} /> :
                        <PPT
                            trigger={trigger}
                            onUpdate={(id, opt, done) => onUpdate({ ...trigger, type: "PlaylistAssignToPlaylistTrigger", playlistId: id, assignOptional: opt, assignDone: done } as PlaylistAssignToPlaylistTrigger)}
                            search={search}
                            posts={posts}
                        />
                    }
                </>
            }
            {trigger.type === "PlaylistUpdateCompetenciesTrigger" &&
                <>
                    {!posts ? <LoaderInline active={true} /> :
                        <PCOT
                            competences={competences}
                            trigger={trigger}
                            onUpdate={(ups) => {
                                onUpdate({ ...trigger, type: "PlaylistUpdateCompetenciesTrigger", competenceUpdates: ups } as PlaylistUpdateCompetenciesTrigger)
                            }}
                            search={search}
                            posts={posts}
                        />
                    }
                </>
            }
            {trigger.type === "PlaylistSendMailOnAssignmentTrigger" &&
                <>
                    {!posts ? <LoaderInline active={true} /> :
                        <PMT
                            trigger={trigger}
                            onUpdate={(ups) => {
                                onUpdate({ ...trigger, type: "PlaylistSendMailOnAssignmentTrigger", mailTemplateId: ups } as PlaylistSendMailOnAssignmentTrigger)
                            }}
                            search={search}
                            posts={posts}
                        />
                    }
                </>
            }
        </C>
    )
}

export default PlaylistEditFinishedTrigger;